import axios from "axios";
import posthog from "posthog-js";
import { config } from "./endPoints";
import { WebStorage, WebStorageNames, messages, Common } from "../utils";
const { ROOT_URL, REST_API } = config;
const { Exception } = messages;

const PUBLIC_AUTH_ROUTES = [
  "login",
  "logout",
  "googleauth",
  "forgotpassword",
  "register",
  "contactus",
  "refreshtoken",
  "facebookauth",
];

const isPublicAuthRoute = (url = "") => {
  const normalizedUrl = String(url).toLowerCase();
  return PUBLIC_AUTH_ROUTES.some((route) => normalizedUrl.endsWith(route));
};

const hasAuthenticatedSession = () => {
  const auth = getAuthToken();
  const isLoggedIn = WebStorage.getLocalStore(WebStorageNames.UserLoggedIn);
  return Boolean(auth?.accessToken && auth?.refreshToken && isLoggedIn);
};

const captureApiException = (err) => {
  if (err?.config?.skipApiExceptionCapture) return;
  if (typeof posthog === "undefined" || !posthog.capture) return;
  const url = err.config?.url ?? "unknown";
  const method = (err.config?.method ?? "unknown").toUpperCase();
  const status = err.response?.status ?? null;
  const message = err.response?.data?.message ?? err.message ?? "Request failed";
  posthog.capture("api_exception", {
    type: "exception",
    url,
    method,
    status,
    message,
  });
};

// Token refresh management
let tokenRefreshTimer = null;
let isRefreshing = false;
let refreshTokenPromise = null; // Promise for the in-flight refresh token request
let refreshTokenFailed = false; // Flag to prevent infinite loops when refresh fails
let failedRefreshAttempts = 0; // Track failed refresh attempts
const MAX_REFRESH_ATTEMPTS = 2; // Maximum number of refresh attempts before giving up

// Function to clear existing timer
const clearTokenRefreshTimer = () => {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
};

// Function to parse UTC date string correctly
// Handles UTC dates from backend to ensure timezone-independent token refresh
const parseUTCDate = (dateString) => {
  if (!dateString) return null;

  // If it's already a number (timestamp in UTC milliseconds), return it directly
  if (typeof dateString === 'number') {
    return dateString;
  }

  // If it's a string, ensure it's parsed as UTC
  if (typeof dateString === 'string') {
    // Check if it already has timezone indicator (Z, +HH:MM, or -HH:MM)
    const hasTimezone = dateString.includes('Z') ||
      /[+-]\d{2}:?\d{2}$/.test(dateString) ||
      /[+-]\d{4}$/.test(dateString);

    let utcString = dateString;

    if (!hasTimezone) {
      // No timezone info found, assume backend sent UTC time and append 'Z'
      // This ensures JavaScript parses it as UTC, not local time
      utcString = dateString.trim() + 'Z';
    }

    const date = new Date(utcString);

    // Validate the date
    if (isNaN(date.getTime())) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("axiosParseDate", {
          type: "exception",
          success: false,
          reason: "Invalid date string",
          dateString: String(dateString),
        });
      }
      return null;
    }

    return date.getTime(); // Returns UTC milliseconds since epoch
  }

  // If it's already a Date object, get its UTC time
  if (dateString instanceof Date) {
    return dateString.getTime();
  }

  if (typeof posthog !== "undefined" && posthog.capture) {
    posthog.capture("axiosParseDate", {
      type: "exception",
      success: false,
      reason: "Unexpected date format",
      typeOf: typeof dateString,
    });
  }
  return null;
};

// Function to set up automatic token refresh
const setupTokenRefresh = (tokenExpire) => {
  clearTokenRefreshTimer();

  if (!tokenExpire) {
    console.warn('No token expiration time provided');
    return;
  }

  // Parse UTC date correctly
  const expirationTime = parseUTCDate(tokenExpire);
  if (!expirationTime) {
    if (typeof posthog !== "undefined" && posthog.capture) {
      posthog.capture("axiosTokenRefresh", {
        type: "exception",
        success: false,
        reason: "Failed to parse token expiration time",
      });
    }
    return;
  }

  // Get current time in UTC milliseconds
  const currentTime = new Date().getTime();
  const timeUntilExpiry = expirationTime - currentTime;
  const refreshTime = timeUntilExpiry - 10000; // Refresh 10 seconds before expiry

  if (refreshTime <= 0) {
    console.warn('Token is already expired or expires very soon');
    console.warn('Expiration time (UTC):', new Date(expirationTime).toISOString());
    console.warn('Current time (UTC):', new Date(currentTime).toISOString());
    console.warn('Time until expiry (ms):', timeUntilExpiry);
    return;
  }

  const refreshTimeSeconds = Math.round(refreshTime / 1000);
  const expirationDate = new Date(expirationTime).toISOString();
  console.log(`Token will be refreshed in ${refreshTimeSeconds} seconds`);
  console.log(`Token expires at (UTC): ${expirationDate}`);

  tokenRefreshTimer = setTimeout(async () => {
    await refreshTokenBeforeExpiry();
  }, refreshTime);
};

// Function to refresh token before expiry
const refreshTokenBeforeExpiry = async () => {
  if (isRefreshing) {
    console.log('Token refresh already in progress');
    return;
  }

  isRefreshing = true;
  console.log('Refreshing token before expiry...');

  try {
    const authData = getAuthToken();
    if (!authData || !authData.refreshToken) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("axiosTokenRefresh", {
          type: "exception",
          success: false,
          reason: "No refresh token available",
        });
      }
      handleTokenRefreshFailure();
      return;
    }

    const response = await issueToken(authData.refreshToken);

    if (response.data && response.data.success && response.data.data) {
      const { accessToken, refreshToken, tokenExpire } = response.data.data;
      const newAuthData = { accessToken, refreshToken, tokenExpire };

      // Update stored auth data
      WebStorage.setLocalStore(WebStorageNames.AuthInfo, newAuthData);
      WebStorage.setLocalStore(WebStorageNames.UserLoggedIn, true);

      console.log('Token refreshed successfully');

      // Set up next refresh if new tokenExpire is provided
      if (tokenExpire) {
        setupTokenRefresh(tokenExpire);
      }
    } else {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("axiosTokenRefresh", {
          type: "exception",
          success: false,
          reason: "Invalid response",
        });
      }
      handleTokenRefreshFailure();
    }
  } catch (error) {
    if (typeof posthog !== "undefined" && posthog.capture) {
      posthog.capture("axiosTokenRefresh", {
        type: "exception",
        success: false,
        reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        status: error?.response?.status,
      });
    }
    handleTokenRefreshFailure();
  } finally {
    isRefreshing = false;
  }
};

// Function to handle token refresh failure
const handleTokenRefreshFailure = () => {
  // Prevent multiple redirects
  if (refreshTokenFailed) {
    return;
  }

  refreshTokenFailed = true;
  failedRefreshAttempts = MAX_REFRESH_ATTEMPTS;
  if (typeof posthog !== "undefined" && posthog.capture) {
    posthog.capture("axiosTokenRefresh", {
      type: "exception",
      success: false,
      reason: "Token refresh failed, clearing user session",
    });
  }

  // Clear all state
  clearTokenRefreshTimer();
  refreshTokenPromise = null;
  isRefreshing = false;

  // Clear storage
  localStorage.clear();
  sessionStorage.clear();

  // Show error and redirect
  Common.showErrorMessage(Exception.sessionExpired);

  // Use setTimeout to prevent multiple redirects
  setTimeout(() => {
    window.location.href = "/";
  }, 100);
};

const axiosBasic = axios.create({
  baseURL: ROOT_URL,
  timeout: 60 * 120 * 1000, // Let's say you want to wait at least 2 hours
  headers: { Pragma: "no-cache" },
});

const issueToken = (refreshToken) =>
  new Promise((resolve, reject) => {
    const headers = {
      "Content-Type": "application/json",
    };

    axiosBasic
      .post(
        `${REST_API.Account.RefreshToken}`,
        {
          RefreshToken: refreshToken,
        },
        { headers }
      )
      .then((data) => {
        const response = data?.data;
        // Success: we have tokens and tokenExpire
        if (response?.success && response?.data?.tokenExpire) {
          setupTokenRefresh(response.data.tokenExpire);
          resolve(data);
          return;
        }
        // Backend returned success: false (e.g. "User does not exist in our system") — logout and clear storage
        if (response && response.success === false) {
          handleTokenRefreshFailure();
          reject(new Error(response.message || "Refresh token failed"));
          return;
        }
        // Invalid or unexpected response — logout
        handleTokenRefreshFailure();
        reject(new Error("Invalid refresh response"));
      })
      .catch((err) => {
        // On any refresh failure (401, 400, network, etc.) log the user out
        handleTokenRefreshFailure();
        reject(err);
      });
  });

export const getAuthToken = () =>
  WebStorage.getLocalStore(WebStorageNames.AuthInfo);

export const getUserInfo = () =>
  WebStorage.getLocalStore(WebStorageNames.UserInfo);

export const configureAxiosDefaults = async () => {
  axiosBasic.defaults.baseURL = ROOT_URL;
  axiosBasic.defaults.headers.post["Content-Type"] = "application/json";

  await axiosBasic.interceptors.request.use(
    (configuration) => {
      const cfg = configuration;
      cfg.headers.Pragma = "no-cache";
      const url = (cfg.url || "").toLowerCase();
      if (isPublicAuthRoute(url)) {
        return cfg;
      }
      const res = getAuthToken();
      if (res !== null) {
        cfg.headers.Authorization = `Bearer ${res.accessToken}`;
      }
      // Super Admin surrogate only: never attach X-Company-Id for Admin/Manager/User
      const roleId = parseInt(WebStorage.getLocalStore(WebStorageNames.RoleId), 10);
      if (roleId === 1) {
        const inCompany = WebStorage.getLocalStore(WebStorageNames.SuperAdminInCompany);
        const isSaCompanyContext = inCompany === true || inCompany === "true";
        if (isSaCompanyContext) {
          let selectedCompany = WebStorage.getLocalStore(WebStorageNames.SelectedCompany);
          if (!selectedCompany) {
            try {
              const raw = localStorage.getItem("selectedCompany");
              selectedCompany = raw ? JSON.parse(raw) : null;
            } catch (_) {
              selectedCompany = null;
            }
          }
          const companyId = selectedCompany?.id || selectedCompany?.Id;
          if (companyId) {
            cfg.headers["X-Company-Id"] = companyId;
          }
        }
      }
      return Promise.resolve(cfg);
    },
    (err) => {
      return Promise.reject(err);
    }
  );
};

export const axiosResponse = async () => {
  await axiosBasic.interceptors.response.use(null, async (err) => {
    // Handle 429 Too Many Requests with retry logic
    if (err.config && err.response && err.response.status === 429) {
      const config = err.config;

      // Check if we've already retried this request
      const retryCount = config.__retryCount || 0;
      const maxRetries = 3; // Maximum number of retries

      if (retryCount >= maxRetries) {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("axiosRateLimit", {
            type: "exception",
            success: false,
            reason: "Max retries reached for 429 error",
            status: 429,
          });
        }
        captureApiException(err);
        Common.showErrorMessage('Too many requests. Please try again later.');
        return Promise.reject(err);
      }

      // Get retry delay from Retry-After header or use exponential backoff
      let retryDelay = 1000; // Default 1 second

      const retryAfter = err.response.headers['retry-after'];
      if (retryAfter) {
        // Retry-After can be in seconds or a date
        const retryAfterNum = parseInt(retryAfter, 10);
        if (!isNaN(retryAfterNum)) {
          retryDelay = retryAfterNum * 1000; // Convert seconds to milliseconds
        } else {
          // If it's a date, calculate the difference
          const retryDate = new Date(retryAfter);
          if (!isNaN(retryDate.getTime())) {
            retryDelay = Math.max(0, retryDate.getTime() - Date.now());
          }
        }
      } else {
        // Exponential backoff: 1s, 2s, 4s
        retryDelay = Math.pow(2, retryCount) * 1000;
      }

      // Ensure minimum delay of 1 second
      retryDelay = Math.max(retryDelay, 1000);

      console.log(`Rate limited (429). Retrying in ${retryDelay / 1000} seconds... (Attempt ${retryCount + 1}/${maxRetries})`);

      // Mark that we're retrying this request
      config.__retryCount = retryCount + 1;

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, retryDelay));

      // Retry the request
      return axiosBasic.request(config);
    }

    if (err.config && err.response && err.response.status === 401) {
      const requestUrl = (err.config.url || "").toLowerCase();

      // Public auth/register endpoints should not trigger global session-expired flow.
      if (isPublicAuthRoute(requestUrl) || !hasAuthenticatedSession()) {
        captureApiException(err);
        return Promise.reject(err);
      }

      // Skip token refresh for refresh token endpoint itself to prevent infinite loop
      if (requestUrl.includes("refreshtoken")) {
        captureApiException(err);
        handleTokenRefreshFailure();
        return Promise.reject(err);
      }

      // If refresh has already failed, don't try again to prevent infinite loop
      if (refreshTokenFailed || failedRefreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("axiosTokenRefresh", {
            type: "exception",
            success: false,
            reason: "Token refresh has failed, redirecting to login",
            status: err?.response?.status,
          });
        }
        captureApiException(err);
        handleTokenRefreshFailure();
        return Promise.reject(err);
      }

      const data = getAuthToken();
      if (data !== null && data.refreshToken) {
        // Semaphore: Only one refresh token request at a time
        // If a refresh is already in progress, wait for it to complete
        if (!refreshTokenPromise) {
          failedRefreshAttempts++;
          refreshTokenPromise = issueToken(data.refreshToken).then((res) => {
            if (res?.data?.data === null || !res?.data?.success) {
              refreshTokenFailed = true;
              failedRefreshAttempts = MAX_REFRESH_ATTEMPTS;
              handleTokenRefreshFailure();
              refreshTokenPromise = null;
              throw new Error('Token refresh failed');
            }
            const { accessToken, refreshToken, tokenExpire } = res.data.data;
            const authData = { accessToken, refreshToken, tokenExpire };
            WebStorage.setLocalStore(WebStorageNames.AuthInfo, authData);
            WebStorage.setLocalStore(WebStorageNames.UserLoggedIn, true);

            // Reset failure flags on successful refresh
            refreshTokenFailed = false;
            failedRefreshAttempts = 0;

            // Set up automatic token refresh if tokenExpire is provided
            if (tokenExpire) {
              setupTokenRefresh(tokenExpire);
            }

            // Clear the promise after successful refresh so future 401s can trigger new refresh
            refreshTokenPromise = null;
            return res;
          }).catch((error) => {
            // Mark refresh as failed to prevent infinite loops
            refreshTokenFailed = true;
            failedRefreshAttempts = MAX_REFRESH_ATTEMPTS;
            refreshTokenPromise = null;
            captureApiException(error);

            // Only handle failure if we haven't already redirected
            if (failedRefreshAttempts >= MAX_REFRESH_ATTEMPTS) {
              handleTokenRefreshFailure();
            }

            throw error;
          });
        }

        // Wait for the existing refresh to complete, then retry the original request
        return refreshTokenPromise.then((res) => {
          // Update the original request config with the new token
          const newAuthData = getAuthToken();
          if (newAuthData && newAuthData.accessToken) {
            err.config.headers.Authorization = `Bearer ${newAuthData.accessToken}`;
          }
          // Reset retry count for this specific request to prevent infinite retries
          if (err.config.__retryCount) {
            err.config.__retryCount = 0;
          }
          return Promise.resolve(axiosBasic.request(err.config));
        }).catch((error) => {
          // Refresh failed — logout so user isn't stuck with repeated 401s
          captureApiException(err);
          handleTokenRefreshFailure();
          return Promise.reject(err);
        });
      } else {
        // No auth token available, redirect to login
        captureApiException(err);
        handleTokenRefreshFailure();
        return Promise.reject(err);
      }
    }
    if (err.message === Exception.networkError) {
      captureApiException(err);
      return Promise.reject(err);
    }
    captureApiException(err);
    return Promise.reject(err);
  });
};

// Function to initialize token refresh system (call this after login)
export const initializeTokenRefresh = () => {
  const authData = getAuthToken();
  if (authData && authData.tokenExpire) {
    setupTokenRefresh(authData.tokenExpire);
  }
};

// Function to clear token refresh system (call this on logout)
export const clearTokenRefresh = () => {
  clearTokenRefreshTimer();
  isRefreshing = false;
  refreshTokenPromise = null; // Clear any pending refresh promise
  refreshTokenFailed = false; // Reset failure flag
  failedRefreshAttempts = 0; // Reset attempt counter
};

// Function to manually refresh token
export const manualTokenRefresh = async () => {
  await refreshTokenBeforeExpiry();
};

export { axiosBasic };

